import React, { useRef } from 'react';
import {
  Box,
  Tooltip,
  VStack,
} from '@chakra-ui/react';
import { LuSparkles, LuMail, LuSend, LuInbox, LuImport, LuPencilLine } from 'react-icons/lu';
import { ChevronRightIcon, ChevronLeftIcon } from '@chakra-ui/icons';
import { motion } from 'framer-motion'; 
import MessageList from './MessageList';
import MessageBubble from './MessageBubble';
import MessageInput from './MessageInput';
import MessageDraftingPanel from './MessageDraftingPanel';
import SequencerExportPanel from './SequencerExportPanel';
import InboxPanel from './InboxPanel';
import ScribelessPanel from './ScribelessPanel';
import CampaignStatusBar from '../CampaignStatusBar';
import CreditSpendGateCard from './CreditSpendGateCard';

const MotionBox = motion.create ? motion.create(Box) : motion(Box);

const ImportBar = ({ onFileUpload, canUpload }) => {
  const fileInputRef = useRef(null);

  const handleUploadClick = () => {
    if (!canUpload) return;
    fileInputRef.current?.click();
  };

  const handleFileChange = (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length > 0 && onFileUpload) {
      onFileUpload(files);
    }
    e.target.value = '';
  };

  return (
    <Box px={4} pt={3} pb={1}>
      <Box
        as="button"
        display="flex"
        alignItems="center"
        gap="6px"
        px={3}
        py={1.5}
        borderRadius="8px"
        border="1px solid rgba(255,255,255,0.08)"
        bg="rgba(255,255,255,0.03)"
        color="rgba(255,255,255,0.5)"
        fontSize="12px"
        fontWeight="500"
        cursor={canUpload ? 'pointer' : 'not-allowed'}
        opacity={canUpload ? 1 : 0.5}
        transition="all 0.15s ease"
        _hover={canUpload ? {
          borderColor: 'rgba(255,255,255,0.18)',
          bg: 'rgba(255,255,255,0.06)',
          color: 'rgba(255,255,255,0.75)',
        } : {}}
        onClick={handleUploadClick}
      >
        <LuImport size={13} />
        Import
      </Box>
      <input
        ref={fileInputRef}
        type="file"
        accept=".csv,.xlsx,.xls,.png,.jpg,.jpeg,.webp"
        multiple
        style={{ display: 'none' }}
        onChange={handleFileChange}
      />
    </Box>
  );
};

const CollapsibleChatPanel = ({
  messages,
  isAgentThinking,
  messagesEndRef,
  inputRef,
  inputMessage,
  onInputChange,
  onSendMessage,
  isLoading,
  isGenerating = false,
  onStop,
  isTextareaAtMaxHeight,
  statusUpdate,
  onStatusComplete,
  statusOperationType,
  editingState,
  isExpanded,
  onToggle,
  isMobile, 
  enableTransitions,
  panelMode = 'chat', // 'chat' | 'message' | 'sequencer' | 'inbox' | 'scribeless'
  onModeChange,
  campaignId,
  onMessageSave,
  onMessagePreview,
  editingDraft = null, // Draft being edited (for "Edit column" functionality)
  onCancelEditDraft,
  onFileUpload,
  isUploading = false,
  canUpload = true,
  onExportTrackingRefresh,
  onGillyreachSequencerStatusRefresh,
  onThreadSelect,
  selectedThreadId = null,
  creditSpendGate = null,
  onCreditSpendGateDecision,
  scribelessOrientation = 'landscape',
  scribelessTemplate = 'signature',
  onScribelessOrientationChange,
  onScribelessTemplateChange,
  onScribelessBrandChange,
  onScribelessNoteChange,
}) => {
  const effectiveIsExpanded = isMobile || isExpanded;
  
  const expandedWidth = 480;
  const notchWidth = 46;
  const gillyReachViolet = "#9d71fd";

  // Handle icon clicks
  const handleSparkleClick = (e) => {
    e.stopPropagation();
    if (panelMode !== 'chat') {
      onModeChange?.('chat');
    }
    // If already in chat mode and collapsed, expand
    if (!effectiveIsExpanded) {
      onToggle?.();
    }
  };

  const handleMessageClick = (e) => {
    e.stopPropagation();
    // Always call onModeChange so editingDraft is cleared for a fresh form,
    // even if panelMode is already 'message' (e.g., after "Edit column")
    onModeChange?.('message');
    // If collapsed, expand
    if (!effectiveIsExpanded) {
      onToggle?.();
    }
  };

  const handleCloseMessageDrafting = () => {
    onModeChange?.('chat');
  };

  const handleSequencerClick = (e) => {
    e.stopPropagation();
    onModeChange?.('sequencer');
    if (!effectiveIsExpanded) {
      onToggle?.();
    }
  };

  const handleInboxClick = (e) => {
    e.stopPropagation();
    onModeChange?.('inbox');
    if (!effectiveIsExpanded) {
      onToggle?.();
    }
  };

  const handleScribelessClick = (e) => {
    e.stopPropagation();
    onModeChange?.('scribeless');
    if (!effectiveIsExpanded) {
      onToggle?.();
    }
  };

  // FLUID SPRING CONFIGURATION - smooth slide, no bounce
  // Matches CampaignChatInterface exactly for synchronized motion
  const fluidSpringTransition = {
    type: "spring",
    stiffness: 300,
    damping: 35,    // Higher = no bounce, smooth stop
    mass: 0.8,
  };

  return (
    <MotionBox
      position="absolute"
      right={0}
      top={0}
      bottom={0}
      w={`${expandedWidth}px`}
      h="100%"
      initial={false}
      animate={{ 
        x: effectiveIsExpanded ? 0 : expandedWidth 
      }}
      transition={enableTransitions ? fluidSpringTransition : { duration: 0 }}
      display="flex"
      flexDirection="row"
      overflow="visible"
      zIndex={1000}
    >
      {/* --- THE NOTCH (Handle) --- */}
      {!isMobile && (
        <Box
          position="absolute"
          left={`-${notchWidth}px`}
          top="15%"
          w={`${notchWidth}px`}
          bg="#111111"
          borderLeft="1px solid rgba(255,255,255,0.10)"
          borderTop="1px solid rgba(255,255,255,0.10)"
          borderBottom="1px solid rgba(255,255,255,0.10)"
          borderLeftRadius="12px"
          py={4}
          boxShadow="-4px 0 10px rgba(0,0,0,0.3)"
          display="flex"
          flexDirection="column"
          alignItems="center"
          zIndex={1001}
        >
          <VStack spacing={4}>
            {/* Sparkle Icon - Chat Mode */}
            <Tooltip 
              label="Ask Gilly AI"
              placement="left"
              hasArrow
              bg="#111111"
              color="#fafafa"
              borderRadius="md"
              fontSize="xs"
              fontWeight="medium"
              px={3}
              py={2}
              border="1px solid rgba(255,255,255,0.10)"
              backdropFilter="blur(8px)"
              boxShadow="0 4px 12px rgba(0,0,0,0.4)"
            >
              <MotionBox
                cursor="pointer"
                onClick={handleSparkleClick}
                animate={{
                  color: panelMode === 'chat' && effectiveIsExpanded ? gillyReachViolet : "#FFFFFF",
                }}
                whileHover={{ 
                  y: -3,
                  color: gillyReachViolet,
                  scale: 1.1
                }}
                transition={{ type: "spring", stiffness: 400, damping: 17 }}
              >
                <LuSparkles size={22} /> 
              </MotionBox>
            </Tooltip>

            {/* Message Icon - Message Drafting Mode */}
            <Tooltip 
              label="Draft Messages"
              placement="left"
              hasArrow
              bg="#111111"
              color="#fafafa"
              borderRadius="md"
              fontSize="xs"
              fontWeight="medium"
              px={3}
              py={2}
              border="1px solid rgba(255,255,255,0.10)"
              backdropFilter="blur(8px)"
              boxShadow="0 4px 12px rgba(0,0,0,0.4)"
            >
              <MotionBox
                cursor="pointer"
                onClick={handleMessageClick}
                animate={{
                  color: panelMode === 'message' && effectiveIsExpanded ? gillyReachViolet : "#FFFFFF",
                }}
                whileHover={{ 
                  y: -3,
                  color: gillyReachViolet,
                  scale: 1.1
                }}
                transition={{ type: "spring", stiffness: 400, damping: 17 }}
              >
                <LuMail size={20} /> 
              </MotionBox>
            </Tooltip>

            {/* Export to Sequencer Icon */}
            <Tooltip
              label="Export to Sequencer"
              placement="left"
              hasArrow
              bg="#111111"
              color="#fafafa"
              borderRadius="md"
              fontSize="xs"
              fontWeight="medium"
              px={3}
              py={2}
              border="1px solid rgba(255,255,255,0.10)"
              backdropFilter="blur(8px)"
              boxShadow="0 4px 12px rgba(0,0,0,0.4)"
            >
              <MotionBox
                cursor="pointer"
                onClick={handleSequencerClick}
                animate={{
                  color: panelMode === 'sequencer' && effectiveIsExpanded ? gillyReachViolet : "#FFFFFF",
                }}
                whileHover={{
                  y: -3,
                  color: gillyReachViolet,
                  scale: 1.1,
                }}
                transition={{ type: "spring", stiffness: 400, damping: 17 }}
              >
                <LuSend size={18} />
              </MotionBox>
            </Tooltip>

            {/* Inbox Icon */}
            <Tooltip
              label="Inbox"
              placement="left"
              hasArrow
              bg="#111111"
              color="#fafafa"
              borderRadius="md"
              fontSize="xs"
              fontWeight="medium"
              px={3}
              py={2}
              border="1px solid rgba(255,255,255,0.10)"
              backdropFilter="blur(8px)"
              boxShadow="0 4px 12px rgba(0,0,0,0.4)"
            >
              <MotionBox
                cursor="pointer"
                onClick={handleInboxClick}
                animate={{
                  color: panelMode === 'inbox' && effectiveIsExpanded ? gillyReachViolet : "#FFFFFF",
                }}
                whileHover={{
                  y: -3,
                  color: gillyReachViolet,
                  scale: 1.1,
                }}
                transition={{ type: "spring", stiffness: 400, damping: 17 }}
              >
                <LuInbox size={19} />
              </MotionBox>
            </Tooltip>

            {/* Handwritten Notes Icon */}
            <Tooltip
              label="Handwritten Notes"
              placement="left"
              hasArrow
              bg="#111111"
              color="#fafafa"
              borderRadius="md"
              fontSize="xs"
              fontWeight="medium"
              px={3}
              py={2}
              border="1px solid rgba(255,255,255,0.10)"
              backdropFilter="blur(8px)"
              boxShadow="0 4px 12px rgba(0,0,0,0.4)"
            >
              <MotionBox
                cursor="pointer"
                onClick={handleScribelessClick}
                animate={{
                  color: panelMode === 'scribeless' && effectiveIsExpanded ? gillyReachViolet : "#FFFFFF",
                }}
                whileHover={{
                  y: -3,
                  color: gillyReachViolet,
                  scale: 1.1,
                }}
                transition={{ type: "spring", stiffness: 400, damping: 17 }}
              >
                <LuPencilLine size={19} />
              </MotionBox>
            </Tooltip>

            {/* Expand/Collapse Arrow */}
            <Box
              color="rgba(255,255,255,0.25)"
              cursor="pointer"
              onClick={onToggle}
              _hover={{ color: 'rgba(255,255,255,0.65)' }}
              transition="color 0.2s ease"
            >
              {effectiveIsExpanded ? <ChevronRightIcon boxSize={5} /> : <ChevronLeftIcon boxSize={5} />}
            </Box>
          </VStack>
        </Box>
      )}

      {/* --- PANEL CONTENT --- */}
      <MotionBox
        w="100%"
        h="100%"
        display="flex"
        flexDirection="column"
        bg="#111111"
        borderLeft={isMobile ? "none" : "1px solid"}
        borderColor="rgba(255,255,255,0.10)"
        
        // Sync opacity with the slide using same spring transition
        initial={false}
        animate={{ 
          opacity: effectiveIsExpanded ? 1 : 0
        }}
        transition={enableTransitions ? fluidSpringTransition : { duration: 0 }}
        
        pointerEvents={effectiveIsExpanded ? "auto" : "none"}
        minH={0}
        maxH="100%"
        overflow="hidden"
        position="relative"
        zIndex={999}
      >
        {/* ── Chat mode ─────────────────────────────────────────── */}
        {panelMode === 'chat' && (
          <>
            {/* Import bar */}
            <ImportBar onFileUpload={onFileUpload} canUpload={canUpload} />

            <MessageList
              messages={messages}
              isAgentThinking={isAgentThinking}
              messagesEndRef={messagesEndRef}
            >
              {messages.map((message) => (
                <MessageBubble
                  key={message.id}
                  message={message}
                  isEditing={editingState.editingMessageId === message.id}
                  editContent={editingState.editingContent}
                  onEditChange={editingState.updateContent}
                  onEditSubmit={editingState.handleSubmitEdit}
                  onEditCancel={editingState.cancelEdit}
                  onStartEdit={editingState.startEdit}
                  editBubbleRef={editingState.editBubbleRef}
                  editTextareaRef={editingState.editTextareaRef}
                />
              ))}
            </MessageList>

            {panelMode === 'chat' && creditSpendGate?.gate_id && creditSpendGate?.kind === 'prompt' && (
              <Box px={3} pb={2}>
                <CreditSpendGateCard
                  gate={creditSpendGate}
                  onDecision={onCreditSpendGateDecision}
                />
              </Box>
            )}

            <MessageInput
              inputRef={inputRef}
              value={inputMessage}
              onChange={onInputChange}
              onSend={onSendMessage}
              isLoading={isLoading}
              isGenerating={isGenerating}
              onStop={onStop}
              isTextareaAtMaxHeight={isTextareaAtMaxHeight}
              isDisabled={!!statusUpdate && !isGenerating}
              onFileUpload={onFileUpload}
              isUploading={isUploading}
              canUpload={canUpload}
            />
          </>
        )}

        {/* ── Message drafting mode ──────────────────────────────── */}
        {panelMode === 'message' && (
          <MessageDraftingPanel
            onClose={editingDraft ? onCancelEditDraft : handleCloseMessageDrafting}
            onSave={onMessageSave}
            onPreview={onMessagePreview}
            campaignId={campaignId}
            editingDraft={editingDraft}
          />
        )}

        {/* Email/custom-field progress while in chat or message drafting (e.g. fanout started from table) */}
        {statusUpdate && (panelMode === 'chat' || panelMode === 'message') && (
          <Box
            position="absolute"
            bottom={panelMode === 'message' ? '100px' : '80px'}
            left="12px"
            right="12px"
            zIndex={30}
            pointerEvents="none"
          >
            <Box pointerEvents="auto">
              <CampaignStatusBar
                statusUpdate={statusUpdate}
                onComplete={onStatusComplete}
                operationType={statusOperationType}
              />
            </Box>
          </Box>
        )}

        {/* ── Sequencer export — always mounted to preserve verification state.
             Hidden via CSS when inactive so polling and all React state survive
             tab switches. Unmounts naturally on hard-refresh or campaign change. ── */}
        <Box
          display={panelMode === 'sequencer' ? 'flex' : 'none'}
          flexDirection="column"
          h="100%"
          w="100%"
          minH={0}
        >
          <SequencerExportPanel
            onClose={() => onModeChange?.('chat')}
            campaignId={campaignId}
            onExportTrackingRefresh={onExportTrackingRefresh}
            onGillyreachSequencerStatusRefresh={onGillyreachSequencerStatusRefresh}
          />
        </Box>

        {/* ── Inbox — always mounted so future unread-count polling survives
             panel switches without re-mounting. ── */}
        <Box
          display={panelMode === 'inbox' ? 'flex' : 'none'}
          flexDirection="column"
          h="100%"
          w="100%"
          minH={0}
        >
          <InboxPanel
            onClose={() => onModeChange?.('chat')}
            campaignId={campaignId}
            onThreadSelect={onThreadSelect}
            selectedThreadId={selectedThreadId}
          />
        </Box>

        {/* ── Handwritten Notes (Scribeless) ── */}
        <Box
          display={panelMode === 'scribeless' ? 'flex' : 'none'}
          flexDirection="column"
          h="100%"
          w="100%"
          minH={0}
        >
          <ScribelessPanel
            onClose={() => onModeChange?.('chat')}
            campaignId={campaignId}
            orientation={scribelessOrientation}
            template={scribelessTemplate}
            onOrientationChange={onScribelessOrientationChange}
            onTemplateChange={onScribelessTemplateChange}
            onBrandPreviewChange={onScribelessBrandChange}
            onNotePreviewChange={onScribelessNoteChange}
          />
        </Box>
      </MotionBox>
    </MotionBox>
  );
};

export default CollapsibleChatPanel;